import jsPDF from 'jspdf';
import { WorkoutProgram } from '@/hooks/useWorkoutPrograms';
import { supabase } from '@/integrations/supabase/client';

export const exportToPDF = async (program: WorkoutProgram) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    let yPosition = margin;

    // Straiv brand colors (RGB values for PDF)
    const straivColors = {
      primary: [31, 212, 212] as [number, number, number], // Turquoise #1FD4D4
      dark: [19, 28, 39] as [number, number, number], // Dark Navy #131C27
      accent: [174, 239, 255] as [number, number, number], // Light Blue #AEEFFF
      text: [34, 34, 34] as [number, number, number], // Dark gray for text
      lightGray: [136, 136, 136] as [number, number, number], // Gray for secondary text
      white: [255, 255, 255] as [number, number, number]
    };

    // Helper function to add logo using the actual STRAIV logo image
    const addLogo = async () => {
      try {
        const logoY = yPosition + 10;
        
        // Import and add the actual STRAIV logo
        const logoModule = await import('@/assets/straiv-logo.png');
        const logoUrl = logoModule.default;
        
        // Convert image to base64 for PDF
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        return new Promise<number>((resolve) => {
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = img.width;
              canvas.height = img.height;
              ctx?.drawImage(img, 0, 0);
              const dataURL = canvas.toDataURL('image/png');
              
              // Calculate logo dimensions - keep aspect ratio
              const logoWidth = 60; // Reasonable width for PDF
              const logoHeight = (img.height / img.width) * logoWidth;
              
              // Add logo to PDF
              doc.addImage(dataURL, 'PNG', margin, logoY, logoWidth, logoHeight);
              
              // Add tagline below logo
              doc.setFontSize(8);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(...straivColors.lightGray);
              doc.text('AI-Powered Fitness Training Programs', margin, logoY + logoHeight + 10);
              
              resolve(logoY + logoHeight + 25);
            } catch (error) {
              console.log('Logo rendering error:', error);
              resolve(yPosition + 55);
            }
          };
          
          img.onerror = () => {
            console.log('Logo loading error');
            resolve(yPosition + 55);
          };
          
          img.src = logoUrl;
        });
      } catch (error) {
        console.log('Logo import error:', error);
        return yPosition + 55;
      }
    };

    // Helper function to add decorative line
    const addDecoLine = (y: number) => {
      doc.setDrawColor(...straivColors.primary);
      doc.setLineWidth(2);
      doc.line(margin, y, pageWidth - margin, y);
      return y + 10;
    };

    // Helper function to add text with compact styling for 5-page limit
    const addStyledText = (
      text: string, 
      x: number, 
      y: number, 
      maxWidth: number, 
      options: {
        fontSize?: number;
        isBold?: boolean;
        color?: [number, number, number];
        style?: 'title' | 'subtitle' | 'body' | 'caption';
      } = {}
    ) => {
      const { fontSize = 10, isBold = false, color = straivColors.text, style = 'body' } = options;
      
      // Compact style presets for 5-page limit
      const stylePresets = {
        title: { fontSize: 16, isBold: true, color: straivColors.primary },
        subtitle: { fontSize: 12, isBold: true, color: straivColors.text },
        body: { fontSize: 10, isBold: false, color: straivColors.text },
        caption: { fontSize: 8, isBold: false, color: straivColors.lightGray }
      };

      if (style !== 'body') {
        const preset = stylePresets[style];
        doc.setFontSize(preset.fontSize);
        doc.setFont('helvetica', preset.isBold ? 'bold' : 'normal');
        doc.setTextColor(...preset.color);
      } else {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        doc.setTextColor(...color);
      }
      
      const splitText = doc.splitTextToSize(text, maxWidth);
      const lineHeight = (style === 'title' ? 18 : style === 'subtitle' ? 14 : 12) * 0.35; // Reduced spacing
      const textHeight = splitText.length * lineHeight;
      
      // Check if we need a new page
      if (y + textHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      
      doc.text(splitText, x, y);
      return y + textHeight + (style === 'title' ? 8 : style === 'subtitle' ? 5 : 3); // Reduced spacing
    };

    // Helper function to add exercise in AI chat format
    const addExercise = (exercise: any, index: number, y: number) => {
      let currentY = y;
      
      // Check if we need space for exercise
      if (currentY + 40 > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }

      // Exercise number and name - AI chat format: "1. **Exercise Name**"
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...straivColors.text);
      const exerciseTitle = `${index + 1}. ${exercise.exercise_name}`;
      currentY = addStyledText(exerciseTitle, margin, currentY, pageWidth - 2 * margin, { isBold: true });
      
      // Exercise details in bullet format like AI chat
      const details = [];
      if (exercise.sets && (exercise.reps_min || exercise.reps_max)) {
        if (exercise.reps_min === exercise.reps_max) {
          details.push(`${exercise.sets} sets x ${exercise.reps_min} reps`);
        } else {
          details.push(`${exercise.sets} sets x ${exercise.reps_min}-${exercise.reps_max} reps`);
        }
      }
      if (exercise.weight_kg) {
        details.push(`Weight: ${exercise.weight_kg}kg`);
      }
      if (exercise.rest_seconds) {
        const restMinutes = Math.floor(exercise.rest_seconds / 60);
        const restSeconds = exercise.rest_seconds % 60;
        if (restMinutes > 0) {
          details.push(`Rest: ${restMinutes}:${restSeconds.toString().padStart(2, '0')} min`);
        } else {
          details.push(`Rest: ${restSeconds}s`);
        }
      }

      // Add details as bullet points
      if (details.length > 0) {
        details.forEach(detail => {
          currentY = addStyledText(`- ${detail}`, margin + 10, currentY, pageWidth - 2 * margin - 10, { style: 'body' });
        });
      }

      // Target muscles as bullet point
      if (exercise.muscle_groups && exercise.muscle_groups.length > 0) {
        currentY = addStyledText(`- Target muscles: ${exercise.muscle_groups.join(', ')}`, margin + 10, currentY, pageWidth - 2 * margin - 10, { style: 'body' });
      }

      // Notes if they exist
      if (exercise.notes && exercise.notes.trim()) {
        currentY = addStyledText(`- Notes: ${exercise.notes}`, margin + 10, currentY, pageWidth - 2 * margin - 10, { style: 'body' });
      }

      return currentY + 5; // Small spacing after exercise
    };

    // Fetch the full AI message content if available
    let fullMessageContent = '';
    if (program.message_id) {
      try {
        const { data: message } = await supabase
          .from('messages')
          .select('content')
          .eq('id', program.message_id)
          .single();
        
        if (message) {
          fullMessageContent = message.content;
        }
      } catch (error) {
        console.log('Could not fetch message content:', error);
      }
    }

    // Start building the PDF - Use Full AI Message Content
    
    // Add logo and header
    yPosition = await addLogo();
    yPosition = addDecoLine(yPosition);

    if (fullMessageContent) {
      // Parse and render the full AI message content
      yPosition = parseAndRenderAIMessage(fullMessageContent, yPosition);
    } else {
      // Fallback to program structure if no message content
      yPosition = renderProgramFallback(program, yPosition);
    }

    // Helper function to parse and render AI message content
    function parseAndRenderAIMessage(content: string, startY: number): number {
      let currentY = startY;
      const lines = content.split('\n');
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (!line && i < lines.length - 1) {
          currentY += 2; // Small gap for empty lines
          continue;
        }
        
        // Handle different markdown elements
        if (line.startsWith('##')) {
          const title = line.replace(/^##\s*/, '');
          currentY = addStyledText(title, margin, currentY, pageWidth - 2 * margin, { style: 'subtitle' });
        } else if (line.startsWith('#')) {
          const title = line.replace(/^#\s*/, '');
          currentY = addStyledText(title, margin, currentY, pageWidth - 2 * margin, { style: 'title' });
        } else if (line.startsWith('###')) {
          const title = line.replace(/^###\s*/, '');
          currentY = addStyledText(title, margin, currentY, pageWidth - 2 * margin, { 
            fontSize: 11, 
            isBold: true, 
            color: straivColors.primary 
          });
        } else if (line.startsWith('**') && line.endsWith('**')) {
          // Bold text
          const text = line.replace(/\*\*/g, '');
          currentY = addStyledText(text, margin, currentY, pageWidth - 2 * margin, { isBold: true });
        } else if (line.startsWith('- ') || line.match(/^\d+\.\s/)) {
          // Bullet points or numbered lists
          currentY = addStyledText(line, margin, currentY, pageWidth - 2 * margin, { style: 'body' });
        } else if (line.startsWith('   - ') || line.startsWith('     -')) {
          // Nested bullet points
          currentY = addStyledText(line, margin + 10, currentY, pageWidth - 2 * margin - 10, { style: 'body' });
        } else if (line.includes('reps') || line.includes('sets') || line.includes('min rest')) {
          // Exercise details
          currentY = addStyledText(`  ${line}`, margin + 15, currentY, pageWidth - 2 * margin - 15, { style: 'body' });
        } else if (line.startsWith('```') || line === '---') {
          // Skip code blocks and separators
          continue;
        } else if (line.length > 0) {
          // Regular text
          currentY = addStyledText(line, margin, currentY, pageWidth - 2 * margin, { style: 'body' });
        }
      }
      
      return currentY;
    }

    // Fallback function for when no message content is available
    function renderProgramFallback(program: WorkoutProgram, startY: number): number {
      let currentY = startY;
      
      // Basic program info
      currentY = addStyledText(program.title, margin, currentY, pageWidth - 2 * margin, { style: 'title' });
      
      if (program.description) {
        currentY = addStyledText(program.description, margin, currentY, pageWidth - 2 * margin, { style: 'body' });
      }
      
      // Render workout days
      for (const day of program.days) {
        currentY = addStyledText(`${day.day_name || `Day ${day.day_number}`}: ${day.workout_title}`, margin, currentY, pageWidth - 2 * margin, { style: 'subtitle' });
        
        for (let i = 0; i < day.exercises.length; i++) {
          currentY = addExercise(day.exercises[i], i, currentY);
        }
        currentY += 5;
      }
      
      return currentY;
    }

    // Add footer to all pages
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Footer line
      doc.setDrawColor(...straivColors.primary);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 25, pageWidth - margin, pageHeight - 25);
      
      // Footer text - properly centered
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...straivColors.lightGray);
      
      // Left-aligned text
      doc.text(
        'Generated by STRAIV AI Coach',
        margin,
        pageHeight - 15
      );
      
      // Right-aligned text (properly calculated)
      const pageText = `Page ${i} of ${totalPages}`;
      const pageTextWidth = doc.getTextWidth(pageText);
      doc.text(
        pageText,
        pageWidth - margin - pageTextWidth,
        pageHeight - 15
      );
    }

    // Save the PDF
    const fileName = `${program.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_straiv_workout.pdf`;
    doc.save(fileName);
    
    return true;
  } catch (error) {
    console.error('PDF Export Error:', error);
    throw new Error('Failed to export PDF. Please try again.');
  }
};